/**
 * ============================================================================
 * GA4 ECOMMERCE EVENT TRACKING
 * ============================================================================
 *
 * Events tracked:
 * - view_item_list  (collection page load)
 * - select_item     (product card click)
 * - view_item       (product page load)
 * - add_to_cart     (all add-to-cart touchpoints)
 * - remove_from_cart (cart drawer + cart page)
 * - view_cart       (cart drawer open + cart page load)
 * - begin_checkout  (checkout button click)
 * - search          (search form submit)
 *
 * Purchase event is handled via Shopify Web Pixel (not in theme)
 */

(function() {
  'use strict';

  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];

  // Helper: format price from cents to decimal
  function formatPrice(priceInCents) {
    return (parseInt(priceInCents, 10) / 100).toFixed(2);
  }

  // Helper: clear ecommerce object before pushing (GA4 best practice)
  function pushEvent(eventName, ecommerceData) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName,
      ecommerce: ecommerceData
    });
  }

  // Helper: build item object from product data
  function buildItem(data) {
    var item = {
      item_id: data.id || '',
      item_name: data.title || '',
      price: data.price ? parseFloat(formatPrice(data.price)) : 0,
      quantity: data.quantity || 1
    };
    if (data.variant_title && data.variant_title !== 'Default Title') {
      item.item_variant = data.variant_title;
    }
    if (data.vendor) item.item_brand = data.vendor;
    if (data.sku) item.item_sku = data.sku;
    if (data.product_type) item.item_category = data.product_type;
    if (data.list_name) item.item_list_name = data.list_name;
    if (typeof data.index === 'number') item.index = data.index;
    return item;
  }

  // Helper: get cart data via fetch
  function getCart() {
    return fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .catch(function() { return null; });
  }

  // Helper: build items array from cart object
  function buildCartItems(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.map(function(item, index) {
      return buildItem({
        id: item.product_id,
        title: item.product_title,
        price: item.final_price,
        quantity: item.quantity,
        variant_title: item.variant_title,
        vendor: item.vendor,
        sku: item.sku,
        product_type: item.product_type,
        index: index
      });
    });
  }

  // ==========================================
  // 1. VIEW ITEM LIST (collection page)
  // ==========================================
  function trackViewItemList() {
    var grid = document.querySelector('[data-ga4-item-list]');
    if (!grid) return;

    var listName = grid.getAttribute('data-ga4-item-list') || 'Collection';
    var cards = grid.querySelectorAll('[data-ga4-item]');
    var items = [];

    cards.forEach(function(card, index) {
      try {
        var data = JSON.parse(card.getAttribute('data-ga4-item'));
        data.list_name = listName;
        data.index = index;
        items.push(buildItem(data));
      } catch(e) {}
    });

    if (items.length > 0) {
      pushEvent('view_item_list', {
        item_list_name: listName,
        items: items
      });
    }
  }

  // ==========================================
  // 2. SELECT ITEM (product card click)
  // ==========================================
  function trackSelectItem() {
    document.addEventListener('click', function(e) {
      var card = e.target.closest('[data-ga4-item]');
      if (!card) return;

      var link = e.target.closest('a');
      if (!link) return;

      try {
        var data = JSON.parse(card.getAttribute('data-ga4-item'));
        var grid = card.closest('[data-ga4-item-list]');
        var listName = grid ? grid.getAttribute('data-ga4-item-list') : '';

        pushEvent('select_item', {
          item_list_name: listName,
          items: [buildItem(data)]
        });
      } catch(e) {}
    });
  }

  // ==========================================
  // 3. VIEW ITEM (product page)
  // ==========================================
  function trackViewItem() {
    var el = document.querySelector('[data-ga4-product]');
    if (!el) return;

    try {
      var data = JSON.parse(el.getAttribute('data-ga4-product'));
      pushEvent('view_item', {
        currency: data.currency || 'EUR',
        value: parseFloat(formatPrice(data.price)),
        items: [buildItem(data)]
      });
    } catch(e) {}
  }

  // ==========================================
  // 4. ADD TO CART (all touchpoints)
  // ==========================================
  function trackAddToCart() {
    // Listen for successful cart additions via custom event
    document.addEventListener('product:added-to-cart', function(e) {
      if (!e.detail) return;
      try {
        var data = e.detail;
        var items = [];
        if (data.id) {
          items.push(buildItem({
            id: data.product_id,
            title: data.product_title,
            price: data.final_price || data.price,
            quantity: data.quantity || 1,
            variant_title: data.variant_title,
            vendor: data.vendor,
            sku: data.sku,
            product_type: data.product_type
          }));
        }
        if (data.items && Array.isArray(data.items)) {
          data.items.forEach(function(item) {
            items.push(buildItem({
              id: item.product_id,
              title: item.product_title,
              price: item.final_price || item.price,
              quantity: item.quantity || 1,
              variant_title: item.variant_title,
              vendor: item.vendor,
              sku: item.sku,
              product_type: item.product_type
            }));
          });
        }
        if (items.length > 0) {
          var totalValue = items.reduce(function(sum, item) {
            return sum + (item.price * item.quantity);
          }, 0);
          pushEvent('add_to_cart', {
            currency: 'EUR',
            value: parseFloat(totalValue.toFixed(2)),
            items: items
          });
        }
      } catch(err) {}
    });

    // Fallback: listen for fetch responses to /cart/add
    var origFetch = window.fetch;
    window.fetch = function(url, options) {
      var result = origFetch.apply(this, arguments);

      try {
        if (typeof url === 'string' && url.includes('/cart/add') && options && options.method === 'POST') {
          result.then(function(response) {
            if (!response.ok) return;
            response.clone().json().then(function(data) {
              document.dispatchEvent(new CustomEvent('product:added-to-cart', { detail: data }));
            }).catch(function() {});
          }).catch(function() {});
        }
      } catch(err) {}

      return result;
    };

    // Also intercept form submissions to /cart/add (PDP main form)
    document.addEventListener('submit', function(e) {
      var form = e.target;
      if (!form || form.getAttribute('action') !== '/cart/add') return;

      var productEl = document.querySelector('[data-ga4-product]');
      if (!productEl) return;

      try {
        var data = JSON.parse(productEl.getAttribute('data-ga4-product'));
        var quantityInput = form.querySelector('[name="quantity"]');
        var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

        // Get selected variant
        var variantInput = form.querySelector('[name="id"]');
        var variantId = variantInput ? variantInput.value : '';

        // Try to find variant data from product JSON
        var productJsonEl = document.querySelector('[data-product-json]');
        if (productJsonEl) {
          var productJson = JSON.parse(productJsonEl.textContent);
          var selectedVariant = productJson.variants.find(function(v) {
            return v.id.toString() === variantId.toString();
          });
          if (selectedVariant) {
            data.price = selectedVariant.price;
            data.variant_title = selectedVariant.title;
            data.sku = selectedVariant.sku;
          }
        }

        data.quantity = quantity;
        pushEvent('add_to_cart', {
          currency: 'EUR',
          value: parseFloat(formatPrice(data.price)) * quantity,
          items: [buildItem(data)]
        });
      } catch(e) {}
    });
  }

  // ==========================================
  // 5. REMOVE FROM CART
  // ==========================================
  function trackRemoveFromCart() {
    document.addEventListener('click', function(e) {
      var removeBtn = e.target.closest('[data-remove-item]');
      if (!removeBtn) return;

      var itemEl = removeBtn.closest('[data-cart-item]');
      if (!itemEl) return;

      var titleEl = itemEl.querySelector('.item-title a, .cart-item-title a');
      var priceEl = itemEl.querySelector('.item-price, .cart-item-price');
      var title = titleEl ? titleEl.textContent.trim() : '';
      var priceText = priceEl ? priceEl.textContent.trim() : '0';
      var price = parseFloat(priceText.replace(/[^0-9,.-]/g, '').replace(',', '.')) || 0;

      pushEvent('remove_from_cart', {
        currency: 'EUR',
        value: price,
        items: [{
          item_name: title,
          price: price,
          quantity: 1
        }]
      });
    });
  }

  // ==========================================
  // 6. VIEW CART
  // ==========================================
  function trackViewCart() {
    // Cart drawer open
    document.addEventListener('click', function(e) {
      if (e.target.closest('[data-cart-drawer-open]') || e.target.closest('.cart-icon-link')) {
        getCart().then(function(cart) {
          if (!cart) return;
          pushEvent('view_cart', {
            currency: 'EUR',
            value: parseFloat(formatPrice(cart.total_price)),
            items: buildCartItems(cart)
          });
        });
      }
    });

    // Cart page load
    if (window.location.pathname === '/cart') {
      getCart().then(function(cart) {
        if (!cart) return;
        pushEvent('view_cart', {
          currency: 'EUR',
          value: parseFloat(formatPrice(cart.total_price)),
          items: buildCartItems(cart)
        });
      });
    }
  }

  // ==========================================
  // 7. BEGIN CHECKOUT
  // ==========================================
  function trackBeginCheckout() {
    document.addEventListener('click', function(e) {
      var checkoutBtn = e.target.closest('[data-checkout-btn], .cart-checkout-button, [href="/checkout"]');
      if (!checkoutBtn) return;

      getCart().then(function(cart) {
        if (!cart) return;
        pushEvent('begin_checkout', {
          currency: 'EUR',
          value: parseFloat(formatPrice(cart.total_price)),
          items: buildCartItems(cart)
        });
      });
    });
  }

  // ==========================================
  // 8. SEARCH
  // ==========================================
  function trackSearch() {
    document.addEventListener('submit', function(e) {
      var form = e.target;
      if (!form || form.getAttribute('action') !== '/search') return;

      var input = form.querySelector('input[name="q"]');
      if (!input || !input.value.trim()) return;

      window.dataLayer.push({
        event: 'search',
        search_term: input.value.trim()
      });
    });
  }

  // ==========================================
  // INIT — wait for DOM
  // ==========================================
  function init() {
    trackViewItemList();
    trackSelectItem();
    trackViewItem();
    trackAddToCart();
    trackRemoveFromCart();
    trackViewCart();
    trackBeginCheckout();
    trackSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
